import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import '@angular/common/locales/global/cs';
import '@angular/common/locales/global/sk';
import {
   APP_ID,
   ApplicationConfig,
   ErrorHandler,
   importProvidersFrom,
   inject,
   provideAppInitializer,
   provideZoneChangeDetection,
} from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '@environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TraceService } from '@sentry/angular';
import { SikoCmsLinkDirective } from '@siko/common';
import { SikoComponentsConfigurationModule } from '@siko/components';
import { SikoOccCartNormalizer } from '@siko/features/cart/occ/adapters/converters/occ-cart-normalizer';
import { SikoOccOrderNormalizer } from '@siko/features/my-account/order-details/occ/converters/occ-order-normalizer';
import { SikoOccB2bUserSerializer } from '@siko/features/organization/occ/serializers/occ-b2b-user-serializer';
import { SikoProductDisponibilityNormalizer } from '@siko/features/product/occ/adapters/converters/product-disponibility-normalizer';
import { B2bCommonModule } from '@siko/shared/b2b-common.module';
import { SikoContentPageMetaResolver } from '@siko/shared/resolvers/content-page-meta.resolver';
import { SikoUserActivityService } from '@siko/shared/services/user-activity.service';
import { CART_NORMALIZER } from '@spartacus/cart/base/root';
import {
   PageMetaResolver,
   PRODUCT_NORMALIZER,
   provideConfig,
   provideConfigFactory,
   provideFeatureToggles,
} from '@spartacus/core';
import { ORDER_NORMALIZER } from '@spartacus/order/root';
import { OccB2bUserSerializer } from '@spartacus/organization/administration/occ';
import { AppRoutingModule } from '@spartacus/storefront';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { SikoB2bCoreModule } from './core.module';
import { LAZY_LOADED_CMS_COMPONENTS, LAZY_LOADED_MODULES } from './siko/configuration/lazy-loaded-modules';
import { GlobalErrorHandler } from './siko/interceptors/global-error.handler';
import { SpartacusModule } from './spartacus/spartacus.module';
import { SikoNewsStoreModule } from '@siko/features/news/store/news-store.module';
import { SikoOrderHistoryStoreModule } from '@siko/features/my-account/order-history/store/order-history-store.module';
import { SikoRemoveCartEntryModule } from '@siko/features/cart/remove-cart-entry/cart.module';
import { SikoProductListModule } from '@siko/features/product/product-listing/product-list/product-list.module';
import { SikoGlobalMessageComponentModule } from '@siko/features/misc/global-message/global-message.module';
import { SikoAsmComponentsModule } from '@siko/features/asm/asm-components.module';
import { SikoTrackingModule } from '@siko/features/tracking/tracking.module';
import * as CustomLayoutConfig from './siko/configuration/layout-config';
import * as CustomCheckoutStepsConfig from './siko/configuration/checkout-steps-config';
import * as CustomRoutingConfig from './siko/configuration/routing-config';
import { CUSTOM_CART_CONFIG } from './siko/configuration/cart-config';
import { MEDIA_CONFIG } from './siko/configuration/media-config';
import { SIKO_PLP_VIEW_CONFIG } from './siko/configuration/plp-product-view-config';
import { SIKO_QUICK_ORDER_CONFIG } from './siko/configuration/quick-order.config';
import { SIKO_PAGINATION_CONFIG } from './siko/configuration/siko-pagination-config';
import { sikoB2BGlobalMessageConfigFactory } from './siko/configuration/global-message.config-factory';
import { SikoB2bRecommendationStoreModule } from '@siko/features/recommendation';

export const config: ApplicationConfig = {
   providers: [
      importProvidersFrom(
         SpartacusModule,
         AppRoutingModule,
         StoreModule.forRoot({}),
         EffectsModule.forRoot([]),
         LazyLoadImageModule,
         SikoB2bCoreModule,
         SikoNewsStoreModule,
         SikoTrackingModule,
         SikoOrderHistoryStoreModule,
         SikoRemoveCartEntryModule,
         SikoGlobalMessageComponentModule,
         SikoCmsLinkDirective,
         SikoProductListModule,
         SikoComponentsConfigurationModule,
         SikoAsmComponentsModule,
         B2bCommonModule,
         SikoCmsLinkDirective,
         BrowserAnimationsModule,
         MatBottomSheetModule,
         SikoB2bRecommendationStoreModule,
         ServiceWorkerModule.register('service-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000',
         })
      ),
      provideHttpClient(withFetch(), withInterceptorsFromDi()),
      provideZoneChangeDetection({ eventCoalescing: true }),
      { provide: APP_ID, useValue: 'spartacus-app' },
      { provide: PageMetaResolver, useExisting: SikoContentPageMetaResolver, multi: true },
      { provide: OccB2bUserSerializer, useExisting: SikoOccB2bUserSerializer },
      { provide: CART_NORMALIZER, useExisting: SikoOccCartNormalizer, multi: true },
      { provide: ORDER_NORMALIZER, useExisting: SikoOccOrderNormalizer, multi: true },
      { provide: PRODUCT_NORMALIZER, useExisting: SikoProductDisponibilityNormalizer, multi: true },
      { provide: ErrorHandler, useClass: GlobalErrorHandler },
      provideAppInitializer(() => {
         inject(TraceService);
      }),
      provideAppInitializer(() => {
         const userActivityService: SikoUserActivityService = inject(SikoUserActivityService);
         return userActivityService.startMonitoringUserActivity();
      }),
      provideConfig({
         featureModules: LAZY_LOADED_MODULES,
         cmsComponents: LAZY_LOADED_CMS_COMPONENTS,
      }),
      provideFeatureToggles({
         authorizationCodeFlowByDefault: true,
         incrementProcessesCountForMergeCart: true,
         dispatchLoginActionOnlyWhenTokenReceived: true,
         cdsLoginEventsToken: true,
         useEnhancedSecurePasswordValidators: true,
         enableRemoveVoucherEndpoint: true,
         showRequiredAsterisks: true,
         enableExpiredRefreshTokenHandlers: true,
         productListItemSummaryReadMore: true,
         showSortFieldsOnlyAtTop: true,
         enableCartReloadOnContextChange: true,
         opfPaymentVerificationCheckProcessingCartOnErrorOnly: true,
         authorizationCodeFlowByDefaultCsrfTokenRefresh: true,
         searchBoxRecentSearchesRemoval: true,
         cdsBottomHeaderSlotAdjustPosition: true,
         redirectOnlyOnTrueNavigationEnd: true,
         pageLinkSanitizeCanonicalUrl: true,
         opfUseDestroyRef: true,
         opfCheckoutUseUpdatePaymentTransaction: true,
         propagateLogoutToAllTabs: true,
         asyncAuthConfigInitializer: false,
         siteIsolationForCustomLoginPage: false,
         applyBaseSiteThemeFromCms: true,
         b2bCheckoutShippingAddressFilter: true,
         improvedTabStyling: true
      }),
      provideConfig(CustomLayoutConfig.CUSTOM_LAYOUT_CONFIG),
      provideConfig(CustomCheckoutStepsConfig.CUSTOM_CHECKOUT_STEPS_CONFIG),
      provideConfig(CustomRoutingConfig.CUSTOM_ROUTING_CONFIG),
      provideConfig(CustomRoutingConfig.CUSTOM_ROUTES_CONFIG),
      provideConfig(CUSTOM_CART_CONFIG),
      provideConfig(MEDIA_CONFIG),
      provideConfig(SIKO_PLP_VIEW_CONFIG),
      provideConfig(SIKO_QUICK_ORDER_CONFIG),
      provideConfig(SIKO_PAGINATION_CONFIG),
      provideConfigFactory(sikoB2BGlobalMessageConfigFactory),
   ],
};
